import * as THREE from 'three';

import { TerrainHeightIndex } from './terrain-height-index.js';
import type { FeatureOverlay, FeaturePart, TerrainTileMesh } from '../../types/terrain-types.js';

const HEIGHT_OFFSET = 2;
/**
 * Shared by GeoJSON and all vector layers. A single raycast or geometry commit
 * is synchronous and can overrun this soft budget; no further work starts then.
 */
const DRAPE_TIME_BUDGET_MS = 4;
const WORK_QUANTUM = 16;

interface DrapeJob {
  overlay: FeatureOverlay;
  isVisible: () => boolean;
  bounds: THREE.Box2;
  dirty: THREE.Box2;
  work?: Generator<void>;
}

/**
 * Incremental draping with local invalidation. Completed hits and misses stay
 * cached in the feature until a tile changes underneath it. Jobs rotate so a
 * large feature or a region without terrain cannot starve other overlays.
 */
export class TerrainDraper {
  private readonly index = new TerrainHeightIndex();
  private readonly jobs = new Map<FeatureOverlay, DrapeJob>();
  private readonly queue = new Set<DrapeJob>();

  constructor(
    private readonly origin: () => THREE.Vector3,
    private readonly now: () => number = () => performance.now(),
  ) {}

  addTile(mesh: TerrainTileMesh): void {
    this.invalidate(this.index.add(mesh));
  }

  removeTile(mesh: TerrainTileMesh): void {
    const bounds = this.index.remove(mesh);
    if (bounds) this.invalidate(bounds);
  }

  register(overlay: FeatureOverlay, isVisible: () => boolean): void {
    this.unregister(overlay);
    // worldXY uses float32 absolute coordinates. Pad the feature bounds to
    // include their rounding error at LV95 magnitudes.
    const bounds = new THREE.Box2(
      new THREE.Vector2(overlay.minX, overlay.minY),
      new THREE.Vector2(overlay.maxX, overlay.maxY),
    ).expandByScalar(1);
    const job: DrapeJob = { overlay, isVisible, bounds, dirty: bounds.clone() };
    this.jobs.set(overlay, job);
    this.queue.add(job);
  }

  unregister(overlay: FeatureOverlay): void {
    const job = this.jobs.get(overlay);
    if (job) this.queue.delete(job);
    this.jobs.delete(overlay);
  }

  /** Call once per frame, after every overlay has queued its new geometry. */
  update(): void {
    if (this.queue.size === 0) return;
    const deadline = this.now() + DRAPE_TIME_BUDGET_MS;
    let hidden = 0;

    while (this.queue.size > 0 && this.now() < deadline) {
      const job = this.queue.values().next().value;
      if (!job) break;
      this.queue.delete(job);
      if (!job.isVisible()) {
        this.queue.add(job);
        if (++hidden >= this.queue.size) break;
        continue;
      }
      hidden = 0;

      if (!job.work) {
        const dirty = job.dirty.clone();
        job.dirty.makeEmpty();
        job.work = this.resolve(job.overlay, dirty);
      }

      for (let i = 0; i < WORK_QUANTUM && this.now() < deadline; i++) {
        if (job.work.next().done) {
          job.work = undefined;
          break;
        }
      }

      if (job.work || !job.dirty.isEmpty()) this.queue.add(job);
    }
  }

  private invalidate(bounds: THREE.Box2): void {
    for (const job of this.jobs.values()) {
      if (!job.bounds.intersectsBox(bounds)) continue;
      job.dirty.union(bounds.clone().intersect(job.bounds));
      this.queue.add(job);
      // Do not restart in-progress work: new changes get a subsequent pass.
    }
  }

  private *resolve(overlay: FeatureOverlay, dirty: THREE.Box2): Generator<void> {
    const originZ = this.origin().z;
    for (const part of overlay.parts) {
      let sampled = false;
      let changed = false;
      for (let i = 0; i < part.count; i++) {
        const x = part.worldXY[i * 2];
        const y = part.worldXY[i * 2 + 1];
        if (x >= dirty.min.x && x <= dirty.max.x && y >= dirty.min.y && y <= dirty.max.y) {
          const hit = this.index.sample(x, y);
          sampled = true;
          part.resolvedZoom[i] = hit?.zoom ?? -1;
          if (hit) changed = setHeight(part, i, hit.z - originZ + HEIGHT_OFFSET) || changed;
        }
        yield;
      }

      if (sampled) {
        changed = (yield* interpolateUnresolvedHeights(part)) || changed;
        if (changed) part.commit();
      }
      yield;
    }
  }
}

function setHeight(part: FeaturePart, index: number, z: number): boolean {
  // Compare after float32 rounding, otherwise unchanged heights cause uploads.
  const height = Math.fround(z);
  if (part.positions[index * 3 + 2] === height) return false;
  part.positions[index * 3 + 2] = height;
  return true;
}

/** Linear interpolation, also resumable so a large ring cannot monopolize a frame. */
function* interpolateUnresolvedHeights(part: FeaturePart): Generator<void, boolean> {
  let previous = -1;
  let changed = false;
  for (let i = 0; i < part.count; i++) {
    if (part.resolvedZoom[i] >= 0) {
      const endZ = part.positions[i * 3 + 2];
      const startZ = previous < 0 ? endZ : part.positions[previous * 3 + 2];
      for (let j = previous + 1; j < i; j++) {
        const t = previous < 0 ? 1 : (j - previous) / (i - previous);
        changed = setHeight(part, j, startZ + (endZ - startZ) * t) || changed;
        yield;
      }
      previous = i;
    }
    yield;
  }
  if (previous >= 0) {
    for (let i = previous + 1; i < part.count; i++) {
      changed = setHeight(part, i, part.positions[previous * 3 + 2]) || changed;
      yield;
    }
  }
  return changed;
}
